import * as React from 'react';
import classnames from 'classnames';
import { ParentSize } from '@visx/responsive';
import { Spinner } from '@ixicare/ui';
import { t } from '~/i18n';
import styles from './Analytics.module.css';
import { AnalyticsWindow } from './analyticsWindow';
import { GraphDefinition, GraphUnit } from './graphs';
import TimeSeriesChart, { ChartSeries } from './TimeSeriesChart';
import { useAnalyticsSeries } from './useAnalyticsSeries';

// Categorical order, validated for colour-vision deficiencies with the brand blue first. Slots
// follow the series' declared position, never what happens to be visible.
const SERIES_COLORS = ['#4286f3', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SINGLE_SERIES = 'value';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function formatDuration(seconds: number): string {
  const rounded = Math.round(seconds);
  if (rounded < MINUTE) return `${rounded}s`;
  if (rounded < HOUR) return `${Math.round(rounded / MINUTE)}m`;
  const [large, largeUnit, small, smallUnit] = rounded < DAY ? [HOUR, 'h', MINUTE, 'm'] : [DAY, 'd', HOUR, 'h'];
  const whole = Math.floor(rounded / large);
  const rest = Math.round((rounded % large) / small);
  return rest ? `${whole}${largeUnit} ${rest}${smallUnit}` : `${whole}${largeUnit}`;
}

// Round durations an axis can step in, so ticks read 15m, 30m, 45m rather than 13m 53s.
const DURATION_STEPS = [
  10,
  30,
  MINUTE,
  5 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  2 * HOUR,
  6 * HOUR,
  12 * HOUR,
  DAY,
  2 * DAY,
  7 * DAY,
  14 * DAY,
  30 * DAY,
];

const durationStep = (max: number) => DURATION_STEPS.find((step) => max / step <= 5) ?? Math.ceil(max / 5 / DAY) * DAY;

const UNITS: Record<GraphUnit, { format: (value: number) => string; tickStep?: (max: number) => number }> = {
  count: { format: (value) => (Number.isInteger(value) ? String(value) : value.toFixed(1)) },
  duration: { format: formatDuration, tickStep: durationStep },
};

type GraphProps = { graph: GraphDefinition; siteUuid: string; timeWindow: AnalyticsWindow };

const Graph: React.FC<GraphProps> = ({ graph, siteUuid, timeWindow }) => {
  const { series, isLoading, isError } = useAnalyticsSeries(graph.family, siteUuid, timeWindow);
  const [hidden, setHidden] = React.useState<Set<string>>(() => new Set());

  const allSeries: ChartSeries[] = series.map((item, index) => {
    const id = item.key ?? SINGLE_SERIES;
    const label = item.key ? graph.seriesLabels?.[item.key] : graph.valueLabel;
    return {
      id,
      label: label ? label() : id,
      color: SERIES_COLORS[index % SERIES_COLORS.length],
      points: item.points,
    };
  });
  const visibleSeries = allSeries.filter((item) => !hidden.has(item.id));
  const hasData = allSeries.some((item) => item.points.length > 0);

  const toggle = (id: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className={styles.CardMessage}>
          <Spinner />
        </div>
      );
    }
    if (isError) return <div className={styles.CardMessage}>{t('statistics.graphLoadError')}</div>;
    if (!hasData) return <div className={styles.CardMessage}>{t('locationTimeline.noData')}</div>;
    return (
      <ParentSize>
        {({ width, height }) => (
          <TimeSeriesChart
            width={width}
            height={height}
            series={visibleSeries}
            chart={graph.chart}
            start={timeWindow.start}
            end={timeWindow.end}
            step={timeWindow.resolution.step}
            formatValue={UNITS[graph.unit].format}
            tickStep={UNITS[graph.unit].tickStep}
            onSelectRange={timeWindow.setRange}
          />
        )}
      </ParentSize>
    );
  };

  return (
    <section className={classnames(styles.Card, graph.size === 'full' ? styles.Full : styles.Half)}>
      <h3 className={styles.CardTitle}>{graph.title()}</h3>
      <div className={styles.CardBody}>{renderBody()}</div>
      {/* A single series is named by the title; a legend only earns its place from two on. */}
      {allSeries.length > 1 && (
        <div className={styles.Legend}>
          {allSeries.map((item) => (
            <button
              key={item.id}
              type="button"
              className={classnames(styles.LegendItem, hidden.has(item.id) && styles.LegendItemHidden)}
              aria-pressed={!hidden.has(item.id)}
              onClick={() => toggle(item.id)}
            >
              <span className={styles.Swatch} style={{ background: item.color }} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

type GraphGridProps = { graphs: GraphDefinition[]; siteUuid: string; timeWindow: AnalyticsWindow };

const GraphGrid: React.FC<GraphGridProps> = ({ graphs, siteUuid, timeWindow }) => (
  <div className={styles.Grid}>
    {graphs.map((graph) => (
      <Graph key={graph.id} graph={graph} siteUuid={siteUuid} timeWindow={timeWindow} />
    ))}
  </div>
);

export default GraphGrid;
